import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { aboutInfo, testimonials } from '@/mocks/data';
import ContactForm from './components/ContactForm';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <SEOHead
        title={`关于我们 - ${aboutInfo.brandName}App官方网站`}
        description={`${aboutInfo.brandName}成立于${aboutInfo.founded}，是一家专注于短视频领域的互联网科技公司。${aboutInfo.description}`}
        keywords="黄瓜视频app,关于黄瓜视频,黄瓜视频官方,黄瓜视频品牌介绍"
        canonicalUrl="https://www.example.com/about"
        ogTitle={`关于我们 - ${aboutInfo.brandName}App官方网站`}
        ogDescription={`${aboutInfo.brandName}成立于${aboutInfo.founded}，专注短视频领域，累计服务超过${aboutInfo.users}用户。`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.example.com/' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
            {
              '@type': 'Organization',
              name: aboutInfo.brandName,
              url: 'https://www.example.com',
              description: aboutInfo.description,
              foundingDate: aboutInfo.founded,
              slogan: aboutInfo.slogan,
              contactPoint: {
                '@type': 'ContactPoint',
                email: aboutInfo.contact.email,
                contactType: 'customer service',
                availableLanguage: ['Chinese'],
              },
              address: {
                '@type': 'PostalAddress',
                addressLocality: '北京',
                addressRegion: '朝阳区',
                streetAddress: aboutInfo.contact.address,
              },
            },
            {
              '@type': 'AboutPage',
              name: `关于我们 - ${aboutInfo.brandName}App官方网站`,
              description: aboutInfo.description,
              url: 'https://www.example.com/about',
            },
          ],
        }}
      />
      <Navbar />
      <main className="min-h-screen pt-20 pb-16 bg-background-50">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <nav className="flex items-center mb-10" aria-label="面包屑导航">
            <Link to="/" className="breadcrumb-link">首页</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">关于我们</span>
          </nav>

          <section className="mb-16 md:mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  品牌故事
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                  关于{aboutInfo.brandName}
                </h1>
                <p className="text-lg text-foreground-700 font-heading mb-4">
                  {aboutInfo.slogan}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  {aboutInfo.description}
                </p>
                <div className="grid grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-background-100 rounded-xl">
                    <p className="text-xl md:text-2xl font-bold text-primary-600">{aboutInfo.founded}</p>
                    <p className="text-xs text-foreground-600 mt-1">成立时间</p>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-xl">
                    <p className="text-xl md:text-2xl font-bold text-accent-600">{aboutInfo.users}</p>
                    <p className="text-xs text-foreground-600 mt-1">累计用户</p>
                  </div>
                  <div className="text-center p-4 bg-background-100 rounded-xl">
                    <p className="text-xl md:text-2xl font-bold text-primary-600">4.9</p>
                    <p className="text-xs text-foreground-600 mt-1">用户评分</p>
                  </div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden h-[340px] md:h-[400px]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20modern%20bright%20tech%20office%20interior%20with%20warm%20natural%20lighting%2C%20creative%20workspace%20with%20greenery%20and%20clean%20minimalist%20design%2C%20young%20team%20collaborating%20in%20a%20relaxed%20atmosphere%2C%20soft%20orange%20and%20green%20accent%20colors%2C%20professional%20yet%20friendly%20vibe&width=700&height=500&seq=about-brand-2026&orientation=landscape"
                  alt="黄瓜视频团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium mb-4">
                使命与愿景
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                {aboutInfo.mission}
              </h2>
              <p className="text-sm text-foreground-600 max-w-[600px] mx-auto">
                愿景：{aboutInfo.vision}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {aboutInfo.values.map((value, index) => (
                <div
                  key={index}
                  className="text-center p-5 bg-background-100 rounded-xl border border-background-200/70"
                >
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                    <i className={`text-lg ${
                      ['ri-heart-line', 'ri-lightbulb-flash-line', 'ri-star-line', 'ri-global-line', 'ri-shield-check-line'][index]
                    }`}></i>
                  </div>
                  <p className="text-sm font-heading font-bold text-foreground-900">{value}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16 md:mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                用户口碑
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                听听用户怎么说
              </h2>
              <p className="text-sm text-foreground-600">来自真实用户的评价</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((item, index) => (
                <div
                  key={index}
                  className="p-6 bg-background-50 border border-background-200/70 rounded-xl"
                >
                  <div className="flex items-center gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${i < item.rating ? 'text-primary-500 ri-star-fill' : 'text-foreground-300 ri-star-line'}`}></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{item.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={item.avatar}
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{item.name}</p>
                      <p className="text-xs text-foreground-500">{item.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="mb-10">
            <div className="max-w-[700px] mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                  有任何问题？随时联系我们
                </h2>
                <p className="text-sm text-foreground-600">
                  我们会在24小时内回复您的消息
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="text-center p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.email}</p>
                </div>
                <div className="text-center p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.wechat}</p>
                </div>
                <div className="text-center p-5 bg-background-100 rounded-xl">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 mb-3">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">公司地址</p>
                  <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.address}</p>
                </div>
              </div>

              <ContactForm />
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}